import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { AGENT_NAV } from "@/lib/agent-nav";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { getAnalysis, saveAnalysisSection, completeAnalysis } from "@/lib/una.functions";
import {
  PRIORITY_GROUPS, computeSummary, HOUSEHOLD_LABEL,
  type UNAPayload, type PriorityKey, type StrategySummary,
} from "@/lib/una.summary";
import { Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/agent/una/$id")({
  head: () => ({
    meta: [
      { title: "Needs Analysis — Strategist Portal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="agent">
      <PortalShell title="Strategist Portal" nav={AGENT_NAV} tourKey="agent_tour">
        <Wizard />
      </PortalShell>
    </ProtectedRoute>
  ),
});

const SECTIONS = [
  { id: "profile", label: "1 · Client Profile" },
  { id: "priorities", label: "2 · Priorities" },
  { id: "finance", label: "3 · Financial Snapshot" },
  { id: "risk", label: "4 · Risk Review" },
  { id: "insurance", label: "5 · Insurance Review" },
  { id: "legacy", label: "6 · Legacy & Estate" },
  { id: "retirement", label: "7 · Retirement" },
  { id: "opportunity", label: "8 · Opportunity Matrix" },
  { id: "action", label: "9 · Action Plan" },
  { id: "summary", label: "✦ Strategy Summary" },
] as const;

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function progressOf(p: UNAPayload): number {
  const checks: boolean[] = [
    !!p.profile?.fullName,
    Object.keys(p.priorities ?? {}).length >= 5,
    !!p.finance?.income || !!p.finance?.expenses,
    !!p.risk?.deathImpact,
    !!p.insurance,
    (p.legacy ?? []).length > 0,
    !!p.retirement?.targetAge,
    !!p.opportunity,
    !!p.actionPlan?.short || !!p.actionPlan?.mid || !!p.actionPlan?.long,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

function NumberField({
  label, value, onChange, prefix = "$",
}: { label: string; value: number | undefined; onChange: (n: number) => void; prefix?: string }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">{label}</Label>
      <div className="mt-1 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-500 font-sans text-[13px]">{prefix}</span>
        <Input
          type="number" inputMode="decimal" className="pl-7"
          value={value ?? ""}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
        />
      </div>
    </div>
  );
}

function SectionCard({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 bg-white border border-bronze-600/20 rounded-[4px] p-6 md:p-8 shadow-soft">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h3 className="mt-3 font-display font-semibold text-navy-800 text-[24px] leading-tight">{title}</h3>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Wizard() {
  const { id } = Route.useParams();
  const get = useServerFn(getAnalysis);
  const save = useServerFn(saveAnalysisSection);
  const complete = useServerFn(completeAnalysis);
  const navigate = useNavigate();

  const [row, setRow] = useState<any>(null);
  const [payload, setPayload] = useState<UNAPayload>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await get({ data: { id } });
        setRow(r.row);
        setPayload((r.row.payload ?? {}) as UNAPayload);
      } catch (e: any) { toast.error(e?.message ?? "Could not load"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const summary: StrategySummary = useMemo(() => computeSummary(payload), [payload]);
  const progress = useMemo(() => progressOf(payload), [payload]);

  const update = useCallback((mut: (draft: UNAPayload) => void) => {
    setPayload((prev) => {
      const next: UNAPayload = JSON.parse(JSON.stringify(prev));
      mut(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSaving(true);
        save({ data: { id, payload: next as any, progressPct: progressOf(next) } })
          .catch((e: any) => toast.error(e?.message ?? "Save failed"))
          .finally(() => setSaving(false));
      }, 700);
      return next;
    });
  }, [id, save]);

  async function handleComplete() {
    try {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      await save({ data: { id, payload: payload as any, progressPct: progressOf(payload) } });
      await complete({ data: { id } });
      toast.success("Analysis completed");
      setRow((r: any) => ({ ...r, status: "completed" }));
      document.getElementById("summary")?.scrollIntoView({ behavior: "smooth" });
    } catch (e: any) { toast.error(e?.message ?? "Could not complete"); }
  }

  if (loading) return <div className="p-10 text-center font-sans text-charcoal-500">Loading…</div>;
  if (!row) return <div className="p-10 text-center font-sans text-charcoal-500">Not found.</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <Link to="/agent/una" className="font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600 hover:text-bronze-400">
            ← All Analyses
          </Link>
          <h2 className="mt-3 font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
            {row.client_name}
          </h2>
          <p className="mt-2 font-sans text-[13px] text-charcoal-500">
            Status: <span className="text-navy-800">{row.status}</span> · {saving ? "Saving…" : "Saved"}
          </p>
        </div>
        <div className="text-right">
          <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-bronze-600">Progress</p>
          <p className="font-display text-[28px] text-navy-800">{progress}%</p>
          <div className="w-48 h-1.5 bg-bronze-600/15 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-bronze-600" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1">
            <Eyebrow>Sections</Eyebrow>
            <nav className="mt-3 flex flex-col">
              {SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="py-1.5 font-sans text-[12px] text-charcoal-500 hover:text-navy-800 border-l-2 border-transparent hover:border-bronze-600 pl-3">
                  {s.label}
                </a>
              ))}
            </nav>
            <div className="mt-6">
              <PrimaryButton onClick={handleComplete}>
                {row.status === "completed" ? "Regenerate Summary" : "Generate Summary"}
              </PrimaryButton>
            </div>
          </div>
        </aside>

        <div className="space-y-6">
          {/* PROFILE */}
          <SectionCard id="profile" eyebrow="Section 1" title="Client Profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full Name" value={payload.profile?.fullName} onChange={(v) => update((d) => { d.profile = { ...d.profile, fullName: v }; })} />
              <Field type="date" label="Date of Birth" value={payload.profile?.dob} onChange={(v) => update((d) => { d.profile = { ...d.profile, dob: v }; })} />
              <Field label="Marital Status" value={payload.profile?.marital} onChange={(v) => update((d) => { d.profile = { ...d.profile, marital: v }; })} />
              <Field label="Occupation" value={payload.profile?.occupation} onChange={(v) => update((d) => { d.profile = { ...d.profile, occupation: v }; })} />
              <Field label="Employer" value={payload.profile?.employer} onChange={(v) => update((d) => { d.profile = { ...d.profile, employer: v }; })} />
              <Field label="State of Residence" value={payload.profile?.state} onChange={(v) => update((d) => { d.profile = { ...d.profile, state: v }; })} />
              <NumberField label="Annual Household Income" value={payload.profile?.annualIncome} onChange={(v) => update((d) => { d.profile = { ...d.profile, annualIncome: v }; })} />
              <NumberField label="Number of Dependents" prefix="#" value={payload.profile?.dependents} onChange={(v) => update((d) => { d.profile = { ...d.profile, dependents: v }; })} />
            </div>
            <div className="mt-8">
              <Eyebrow>Family Structure</Eyebrow>
              <div className="mt-3 space-y-2">
                {(payload.family ?? []).map((m, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-4" placeholder="Name" value={m.name} onChange={(e) => update((d) => { d.family![i].name = e.target.value; })} />
                    <Input className="col-span-2" type="number" placeholder="Age" value={m.age ?? ""} onChange={(e) => update((d) => { d.family![i].age = Number(e.target.value) || undefined; })} />
                    <Input className="col-span-3" placeholder="Relationship" value={m.relationship ?? ""} onChange={(e) => update((d) => { d.family![i].relationship = e.target.value; })} />
                    <label className="col-span-2 flex items-center gap-2 font-sans text-[12px] text-charcoal-500">
                      <Checkbox checked={!!m.dependent} onCheckedChange={(v) => update((d) => { d.family![i].dependent = !!v; })} />
                      Dependent
                    </label>
                    <button className="col-span-1 text-charcoal-500 hover:text-danger" onClick={() => update((d) => { d.family!.splice(i, 1); })}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  className="mt-2 inline-flex items-center gap-2 font-sans text-[12px] text-bronze-600 hover:text-bronze-400"
                  onClick={() => update((d) => { d.family = [...(d.family ?? []), { name: "", relationship: "" }]; })}
                >
                  <Plus className="w-4 h-4" /> Add family member
                </button>
              </div>
            </div>
          </SectionCard>

          {/* PRIORITIES */}
          <SectionCard id="priorities" eyebrow="Section 2" title="Client Priorities Assessment">
            <p className="font-sans text-[13px] text-charcoal-500">Rank each from 1 (not important) to 10 (extremely important).</p>
            <div className="mt-6 space-y-8">
              {PRIORITY_GROUPS.map((g) => (
                <div key={g.group}>
                  <p className="font-display text-[16px] text-navy-800">{g.group}</p>
                  <div className="mt-3 space-y-3">
                    {g.items.map((it) => {
                      const v = payload.priorities?.[it.key] ?? 0;
                      return (
                        <div key={it.key} className="grid grid-cols-12 items-center gap-3">
                          <span className="col-span-6 md:col-span-5 font-sans text-[13px] text-charcoal-900">{it.label}</span>
                          <input
                            type="range" min={0} max={10} step={1} value={v}
                            className="col-span-5 md:col-span-6 accent-bronze-600"
                            onChange={(e) => update((d) => {
                              d.priorities = { ...(d.priorities ?? {}), [it.key]: Number(e.target.value) };
                            })}
                          />
                          <span className="col-span-1 text-right font-display text-[16px] text-bronze-600">{v}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* FINANCE */}
          <SectionCard id="finance" eyebrow="Section 3" title="Current Financial Snapshot">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FinanceBlock title="Monthly Income" fields={["primary", "secondary", "passive"]} labels={{ primary: "Primary", secondary: "Secondary", passive: "Passive" }}
                values={payload.finance?.income ?? {}}
                onChange={(k, v) => update((d) => { d.finance = { ...d.finance, income: { ...d.finance?.income, [k]: v } }; })} />
              <FinanceBlock title="Monthly Expenses" fields={["living", "mortgage", "debt", "insurance", "other"]}
                labels={{ living: "Living", mortgage: "Mortgage/Rent", debt: "Debt payments", insurance: "Insurance premiums", other: "Other" }}
                values={payload.finance?.expenses ?? {}}
                onChange={(k, v) => update((d) => { d.finance = { ...d.finance, expenses: { ...d.finance?.expenses, [k]: v } }; })} />
              <FinanceBlock title="Assets" fields={["checking", "savings", "retirement", "brokerage", "real_estate", "business", "crypto", "other"]}
                labels={{ checking: "Checking", savings: "Savings", retirement: "Retirement", brokerage: "Brokerage", real_estate: "Real Estate", business: "Business Ownership", crypto: "Crypto", other: "Other" }}
                values={payload.finance?.assets ?? {}}
                onChange={(k, v) => update((d) => { d.finance = { ...d.finance, assets: { ...d.finance?.assets, [k]: v } }; })} />
              <FinanceBlock title="Liabilities" fields={["mortgage", "auto", "credit_cards", "student", "business", "other"]}
                labels={{ mortgage: "Mortgage", auto: "Auto loans", credit_cards: "Credit cards", student: "Student loans", business: "Business debt", other: "Other" }}
                values={payload.finance?.liabilities ?? {}}
                onChange={(k, v) => update((d) => { d.finance = { ...d.finance, liabilities: { ...d.finance?.liabilities, [k]: v } }; })} />
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Net Monthly" value={fmt(summary.totals.monthlyIncome - summary.totals.monthlyExpenses)} />
              <Stat label="Total Assets" value={fmt(summary.totals.totalAssets)} />
              <Stat label="Total Liabilities" value={fmt(summary.totals.totalLiabilities)} />
              <Stat label="Net Worth" value={fmt(summary.totals.netWorth)} />
            </div>
          </SectionCard>

          {/* RISK */}
          <SectionCard id="risk" eyebrow="Section 4" title="Risk Management Review">
            <RadioGroup
              label="If you died tomorrow, your family would be…"
              options={[
                { v: "secure", l: "Financially secure" },
                { v: "lt_1yr", l: "Could survive less than 1 year" },
                { v: "1_5yr", l: "Could survive 1–5 years" },
                { v: "crisis", l: "Would need immediate financial assistance" },
              ]}
              value={payload.risk?.deathImpact}
              onChange={(v) => update((d) => { d.risk = { ...d.risk, deathImpact: v as any }; })}
            />
            <RadioGroup
              label="If unable to work for 6 months…"
              options={[
                { v: "none", l: "No impact" }, { v: "moderate", l: "Moderate impact" },
                { v: "severe", l: "Severe impact" }, { v: "crisis", l: "Financial crisis" },
              ]}
              value={payload.risk?.disabilityImpact}
              onChange={(v) => update((d) => { d.risk = { ...d.risk, disabilityImpact: v as any }; })}
            />
            <CheckboxGroup
              label="Long-Term Care planning includes:"
              options={["Home care", "Assisted living", "Nursing facility care", "No plan"]}
              value={payload.risk?.ltcPlan ?? []}
              onChange={(v) => update((d) => { d.risk = { ...d.risk, ltcPlan: v }; })}
            />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField label="Current Emergency Fund" value={payload.risk?.emergencyFund} onChange={(v) => update((d) => { d.risk = { ...d.risk, emergencyFund: v }; })} />
              <RadioGroup
                label="Months covered"
                options={[
                  { v: "0_3", l: "0–3" }, { v: "3_6", l: "3–6" },
                  { v: "6_12", l: "6–12" }, { v: "12+", l: "12+" },
                ]}
                value={payload.risk?.monthsCovered}
                onChange={(v) => update((d) => { d.risk = { ...d.risk, monthsCovered: v as any }; })}
              />
            </div>
          </SectionCard>

          {/* INSURANCE */}
          <SectionCard id="insurance" eyebrow="Section 5" title="Current Insurance Review">
            <div>
              <Eyebrow>Life Insurance Policies</Eyebrow>
              <div className="mt-3 space-y-2">
                {(payload.insurance?.life ?? []).map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-3" placeholder="Carrier" value={p.carrier ?? ""} onChange={(e) => update((d) => { d.insurance!.life![i].carrier = e.target.value; })} />
                    <Input className="col-span-3" placeholder="Type" value={p.type ?? ""} onChange={(e) => update((d) => { d.insurance!.life![i].type = e.target.value; })} />
                    <Input className="col-span-3" type="number" placeholder="Benefit" value={p.benefit ?? ""} onChange={(e) => update((d) => { d.insurance!.life![i].benefit = Number(e.target.value) || undefined; })} />
                    <Input className="col-span-2" type="number" placeholder="Premium" value={p.premium ?? ""} onChange={(e) => update((d) => { d.insurance!.life![i].premium = Number(e.target.value) || undefined; })} />
                    <button className="col-span-1 text-charcoal-500 hover:text-danger" onClick={() => update((d) => { d.insurance!.life!.splice(i, 1); })}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  className="mt-2 inline-flex items-center gap-2 font-sans text-[12px] text-bronze-600 hover:text-bronze-400"
                  onClick={() => update((d) => { d.insurance = { ...d.insurance, life: [...(d.insurance?.life ?? []), {}] }; })}
                >
                  <Plus className="w-4 h-4" /> Add policy
                </button>
              </div>
              <div className="mt-4 max-w-xs">
                <NumberField label="Life Coverage Gap" value={payload.insurance?.lifeGap} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, lifeGap: v }; })} />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Eyebrow>Health Insurance</Eyebrow>
                <div className="mt-3 space-y-2">
                  <Field label="Carrier" value={payload.insurance?.health?.carrier} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, health: { ...d.insurance?.health, carrier: v } }; })} />
                  <NumberField label="Deductible" value={payload.insurance?.health?.deductible} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, health: { ...d.insurance?.health, deductible: v } }; })} />
                  <NumberField label="Out-of-pocket max" value={payload.insurance?.health?.oop} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, health: { ...d.insurance?.health, oop: v } }; })} />
                  <div>
                    <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Coverage Gaps</Label>
                    <Textarea rows={2} value={payload.insurance?.health?.gaps ?? ""} onChange={(e) => update((d) => { d.insurance = { ...d.insurance, health: { ...d.insurance?.health, gaps: e.target.value } }; })} />
                  </div>
                </div>
              </div>
              <div>
                <Eyebrow>Disability</Eyebrow>
                <div className="mt-3 space-y-2">
                  <NumberField label="Monthly Benefit" value={payload.insurance?.disability?.monthlyBenefit} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, disability: { ...d.insurance?.disability, monthlyBenefit: v } }; })} />
                  <Field label="Waiting period" value={payload.insurance?.disability?.waiting} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, disability: { ...d.insurance?.disability, waiting: v } }; })} />
                  <NumberField label="Coverage Gap" value={payload.insurance?.disability?.gap} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, disability: { ...d.insurance?.disability, gap: v } }; })} />
                </div>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <RadioGroup label="Long-Term Care" options={[{ v: "existing", l: "Existing coverage" }, { v: "none", l: "No coverage" }]}
                value={payload.insurance?.ltc} onChange={(v) => update((d) => { d.insurance = { ...d.insurance, ltc: v as any }; })} />
              <CheckboxGroup label="Business Insurance" options={["Key Person", "Buy-Sell Funding", "Disability Buyout", "Executive Bonus", "None"]}
                value={payload.insurance?.business ?? []}
                onChange={(v) => update((d) => { d.insurance = { ...d.insurance, business: v }; })} />
            </div>
          </SectionCard>

          {/* LEGACY */}
          <SectionCard id="legacy" eyebrow="Section 6" title="Legacy & Estate Review">
            <CheckboxGroup
              label="Currently in place"
              options={[
                "Last Will & Testament", "Revocable Trust", "Irrevocable Trust",
                "Power of Attorney", "Healthcare Directive", "Beneficiary Designations Updated",
                "Business Succession Plan", "Family Governance Structure",
              ]}
              value={payload.legacy ?? []}
              onChange={(v) => update((d) => { d.legacy = v; })}
            />
          </SectionCard>

          {/* RETIREMENT */}
          <SectionCard id="retirement" eyebrow="Section 7" title="Retirement Readiness">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField label="Desired retirement age" prefix="#" value={payload.retirement?.targetAge} onChange={(v) => update((d) => { d.retirement = { ...d.retirement, targetAge: v }; })} />
              <NumberField label="Desired retirement income (annual)" value={payload.retirement?.desiredIncome} onChange={(v) => update((d) => { d.retirement = { ...d.retirement, desiredIncome: v }; })} />
              <NumberField label="Current retirement savings" value={payload.retirement?.currentSavings} onChange={(v) => update((d) => { d.retirement = { ...d.retirement, currentSavings: v }; })} />
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Estimated Gap (25× rule)</Label>
                <p className="mt-3 font-display text-[24px] text-navy-800">{fmt(summary.totals.retirementGap)}</p>
              </div>
            </div>
            <div className="mt-6">
              <RadioGroup label="Primary retirement concern"
                options={[
                  { v: "money", l: "Running out of money" }, { v: "volatility", l: "Market volatility" },
                  { v: "taxes", l: "Taxes" }, { v: "healthcare", l: "Healthcare expenses" },
                  { v: "legacy", l: "Leaving a legacy" }, { v: "inflation", l: "Inflation" },
                ]}
                value={payload.retirement?.concern}
                onChange={(v) => update((d) => { d.retirement = { ...d.retirement, concern: v }; })}
              />
            </div>
          </SectionCard>

          {/* OPPORTUNITY */}
          <SectionCard id="opportunity" eyebrow="Section 8" title="Client Opportunity Matrix">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CheckboxGroup label="Protection" options={["Term Life", "Whole Life", "Universal Life", "Indexed Universal Life", "Disability", "Long-Term Care", "Final Expense"]}
                value={payload.opportunity?.protection ?? []}
                onChange={(v) => update((d) => { d.opportunity = { ...d.opportunity, protection: v }; })} />
              <CheckboxGroup label="Wealth Accumulation" options={["Cash Value Life", "Annuities", "Fixed Indexed Annuities", "Retirement Planning", "Tax Diversification"]}
                value={payload.opportunity?.accumulation ?? []}
                onChange={(v) => update((d) => { d.opportunity = { ...d.opportunity, accumulation: v }; })} />
              <CheckboxGroup label="Legacy Planning" options={["Trust Planning", "Estate Planning", "Beneficiary Review", "Wealth Transfer", "Charitable Planning"]}
                value={payload.opportunity?.legacy ?? []}
                onChange={(v) => update((d) => { d.opportunity = { ...d.opportunity, legacy: v }; })} />
              <CheckboxGroup label="Business Planning" options={["Key Person", "Buy-Sell Funding", "Executive Bonus", "Deferred Comp", "Business Continuation"]}
                value={payload.opportunity?.business ?? []}
                onChange={(v) => update((d) => { d.opportunity = { ...d.opportunity, business: v }; })} />
            </div>
          </SectionCard>

          {/* ACTION */}
          <SectionCard id="action" eyebrow="Section 9" title="Recommended Action Plan">
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Immediate Priorities (0–30 days)</Label>
                <Textarea rows={4} value={payload.actionPlan?.short ?? ""} onChange={(e) => update((d) => { d.actionPlan = { ...d.actionPlan, short: e.target.value }; })} />
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Mid-Term Priorities (30–90 days)</Label>
                <Textarea rows={4} value={payload.actionPlan?.mid ?? ""} onChange={(e) => update((d) => { d.actionPlan = { ...d.actionPlan, mid: e.target.value }; })} />
              </div>
              <div>
                <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Long-Term Priorities (90+ days)</Label>
                <Textarea rows={4} value={payload.actionPlan?.long ?? ""} onChange={(e) => update((d) => { d.actionPlan = { ...d.actionPlan, long: e.target.value }; })} />
              </div>
            </div>
          </SectionCard>

          {/* SUMMARY */}
          <SectionCard id="summary" eyebrow="Strategy Summary" title="Agent Strategy Summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Household" value={HOUSEHOLD_LABEL[summary.householdType]} />
              <Stat label="Monthly Capacity" value={fmt(summary.monthlyCapacity)} />
              <Stat label="Net Worth" value={fmt(summary.totals.netWorth)} />
              <Stat label="Retirement Gap" value={fmt(summary.totals.retirementGap)} />
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              <SummaryList title="Top Goals" items={summary.topPriorities.map((p) => p.label)} empty="Rank priorities in §2" />
              <SummaryList title="Biggest Risks" items={summary.topRisks} empty="Complete §3 and §4" />
              <SummaryList title="Recommended Solutions" items={summary.recommendedSolutions} empty="Select opportunities in §8" />
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <SummaryList title="Implementation Order" items={summary.implementationOrder} numbered empty="Complete analysis to generate" />
              <SummaryList title={`Decision Tree · ${HOUSEHOLD_LABEL[summary.householdType]}`} items={summary.decisionTree} />
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PrimaryButton onClick={handleComplete}>
                {row.status === "completed" ? "Regenerate & Save" : "Complete & Save Summary"}
              </PrimaryButton>
              <button
                onClick={() => window.print()}
                className="px-4 py-2 border border-bronze-600 text-bronze-600 font-sans text-[12px] font-semibold uppercase tracking-[0.12em] hover:bg-bronze-600 hover:text-navy-900 rounded-[2px]"
              >
                Print / Save PDF
              </button>
              <button
                onClick={() => navigate({ to: "/agent/una" })}
                className="font-sans text-[12px] text-charcoal-500 hover:text-navy-800"
              >
                Back to list
              </button>
            </div>
            <p className="mt-6 font-sans text-[11px] text-charcoal-500 leading-relaxed">
              Projections shown are hypothetical and for educational purposes only. They do not represent guaranteed returns or actual policy performance. Currency Thoughts LLC is an independent insurance agency; nothing here constitutes legal, tax, or investment advice.
            </p>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = "text",
}: { label: string; value: string | undefined; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-navy-900 border border-bronze-600/40 rounded-[4px] p-4 text-center">
      <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.22em] text-bronze-400">{label}</p>
      <p className="mt-2 font-display font-semibold text-bronze-400 text-[20px] leading-tight">{value}</p>
    </div>
  );
}

function RadioGroup<T extends string>({
  label, options, value, onChange,
}: { label: string; options: { v: T; l: string }[]; value: T | undefined; onChange: (v: T) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">{label}</Label>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 border rounded-[2px] font-sans text-[12px] transition-colors ${
              value === o.v
                ? "bg-navy-800 text-cream-50 border-navy-800"
                : "border-bronze-600/40 text-charcoal-900 hover:border-bronze-600"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckboxGroup({
  label, options, value, onChange,
}: { label: string; options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div>
      <Label className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">{label}</Label>
      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((o) => {
          const checked = value.includes(o);
          return (
            <label key={o} className="flex items-center gap-2 font-sans text-[13px] text-charcoal-900">
              <Checkbox checked={checked} onCheckedChange={(v) => {
                if (v) onChange([...value, o]);
                else onChange(value.filter((x) => x !== o));
              }} />
              {o}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function FinanceBlock({
  title, fields, labels, values, onChange,
}: {
  title: string; fields: string[]; labels: Record<string, string>;
  values: Record<string, number | undefined>;
  onChange: (k: string, v: number) => void;
}) {
  const total = fields.reduce((s, k) => s + (Number(values[k]) || 0), 0);
  return (
    <div>
      <Eyebrow>{title}</Eyebrow>
      <div className="mt-3 space-y-2">
        {fields.map((k) => (
          <NumberField key={k} label={labels[k] ?? k} value={values[k]} onChange={(v) => onChange(k, v)} />
        ))}
        <div className="pt-2 border-t border-bronze-600/20 flex items-baseline justify-between">
          <span className="font-sans text-[11px] uppercase tracking-[0.12em] text-charcoal-500">Total</span>
          <span className="font-display text-[18px] text-navy-800">{fmt(total)}</span>
        </div>
      </div>
    </div>
  );
}

function SummaryList({ title, items, empty, numbered }: { title: string; items: string[]; empty?: string; numbered?: boolean }) {
  return (
    <div className="bg-cream-50 border border-bronze-600/20 rounded-[4px] p-5">
      <Eyebrow>{title}</Eyebrow>
      {items.length === 0 ? (
        <p className="mt-3 font-sans text-[12px] text-charcoal-500 italic">{empty ?? "—"}</p>
      ) : (
        <ol className={`mt-3 space-y-1.5 ${numbered ? "list-decimal pl-5" : "list-disc pl-5"}`}>
          {items.map((it, i) => (
            <li key={i} className="font-sans text-[13px] text-charcoal-900 leading-relaxed">{it}</li>
          ))}
        </ol>
      )}
    </div>
  );
}